import { logger } from "@/utils/logger";
export interface VehicleInventoryItem {
  status?: string; // 'new', 'used', 'certified'
  'Vehicle Type'?: string;
  fuel_type?: string;
  'Final Price'?: number;
  payments?: string; // URL to payment calculation API
  [key: string]: any; // Allow other properties
}

export interface InventoryFilterCriteria {
  status?: string | string[];
  vehicleType?: string | string[];
  fuelType?: string | string[];
  minPrice?: number;
  maxPrice?: number;
}

// In-memory storage for inventory (per session)
let sessionInventory: VehicleInventoryItem[] | null = null;
let inventoryLoadTimestamp: number | null = null;

const INVENTORY_CACHE_KEY = 'chatboost_inventory_cache';
const INVENTORY_TIMESTAMP_KEY = 'chatboost_inventory_timestamp';
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

// Load inventory from localStorage cache
const loadFromCache = (): boolean => {
  try {
    const cached = localStorage.getItem(INVENTORY_CACHE_KEY);
    const timestamp = localStorage.getItem(INVENTORY_TIMESTAMP_KEY);
    
    if (!cached || !timestamp) {
      return false;
    }
    
    const cacheAge = Date.now() - parseInt(timestamp, 10);
    if (cacheAge > CACHE_DURATION) {
      return false;
    }
    
    sessionInventory = JSON.parse(cached);
    inventoryLoadTimestamp = parseInt(timestamp, 10);
    return true;
  } catch (error) {
    console.error('❌ Error loading inventory from cache:', error);
    return false;
  }
};

// Save inventory to localStorage cache
const saveToCache = (inventory: VehicleInventoryItem[]): void => {
  try {
    localStorage.setItem(INVENTORY_CACHE_KEY, JSON.stringify(inventory));
    localStorage.setItem(INVENTORY_TIMESTAMP_KEY, Date.now().toString());
  } catch (error) {
    console.error('❌ Error saving inventory to cache:', error);
  }
};

export const loadInventoryForSession = async (): Promise<boolean> => {
  try {
    // Check if already loaded in this session
    if (sessionInventory && inventoryLoadTimestamp) {
      return true;
    }

    // Try loading from cache first (instant)
    if (loadFromCache()) {
      // Still fetch fresh data in background but return success immediately
      fetchFreshInventory();
      return true;
    }

    // No cache available, fetch from network
    return await fetchFreshInventory();
  } catch (error) {
    console.error('❌ Error loading inventory:', error);
    return false;
  }
};

// Fetch fresh inventory from network
const fetchFreshInventory = async (): Promise<boolean> => {
  try {
    const inventoryUrl = `${window.location.origin}/js/json/chatboost/inventory/inventory-index.json`;
    
    const response = await fetch(inventoryUrl);
    if (!response.ok) {
      console.error('❌ Failed to fetch inventory:', response.status);
      return false;
    }

    const inventoryData = await response.json();
    
    // Store in memory for this session
    sessionInventory = Array.isArray(inventoryData) ? inventoryData : [];
    inventoryLoadTimestamp = Date.now();
    
    // Save to localStorage cache for next page load
    saveToCache(sessionInventory);
    
    return true;
  } catch (error) {
    console.error('❌ Error fetching fresh inventory:', error);
    return false;
  }
};

export const inventoryRetrieval = (criteria: InventoryFilterCriteria): VehicleInventoryItem[] => {
  if (!sessionInventory) {
    logger.warn('⚠️ Inventory not loaded. Call loadInventoryForSession first.');
    return [];
  }

  const filtered = sessionInventory.filter(vehicle => {
    // Filter by status (treat demo units as "new")
    if (criteria.status) {
      const statuses = Array.isArray(criteria.status) ? criteria.status : [criteria.status];
      const vehicleStatus = (vehicle.status || '').toLowerCase();
      const statusVariants: Record<string, string[]> = {
        'new': ['new', 'demo', 'démo', 'demo ', 'demonstrator', 'démonstrateur'],
      };
      const allVariants = statuses.flatMap(s => {
        const sl = s.toLowerCase();
        return statusVariants[sl] || [sl];
      });
      if (!vehicleStatus || !allVariants.some(v => vehicleStatus.includes(v))) {
        return false;
      }
    }

    // Filter by vehicle type - use same logic as search-inventory edge function
    if (criteria.vehicleType) {
      const types = Array.isArray(criteria.vehicleType) ? criteria.vehicleType : [criteria.vehicleType];
      let vehicleType = (vehicle['Vehicle Type'] || '').toLowerCase().trim();
      const vehicleModel = (vehicle.model || vehicle.Model || '').toLowerCase().trim();
      
      // Body type aliases matching the edge function logic
      const bodyVariants: Record<string, string[]> = {
        'sedan': ['sedan', 'car', 'passenger', '4dr. sedan'],
        'suv': ['suv', 'crossover', 'utility', 'cuv'],
        'truck': ['truck', 'pickup'],
        'trucks': ['truck', 'pickup'],
        'minivan': ['minivan', 'van'],
        'minivans': ['minivan', 'van'],
        'hatchback': ['hatchback', 'hatch'],
      };
      
      // Known SUV/crossover models (same list as search-inventory edge function)
      const knownSuvModels = [
        'compass', 'cherokee', 'grand cherokee', 'wrangler', 'renegade', 'gladiator',
        'escape', 'explorer', 'expedition', 'bronco', 'edge', 'ecosport',
        'rav4', 'highlander', '4runner', 'sequoia', 'venza', 'c-hr', 'corolla cross', 'grand highlander',
        'bz4x', 'bz', 'crown signia',
        'cr-v', 'hr-v', 'pilot', 'passport', 'prologue',
        'tucson', 'santa fe', 'palisade', 'kona', 'venue', 'ioniq 5', 'ioniq 6',
        'sportage', 'sorento', 'telluride', 'seltos', 'niro', 'ev6', 'ev9',
        'cx-5', 'cx-50', 'cx-30', 'cx-9', 'cx-90', 'cx-70',
        'outback', 'forester', 'ascent', 'crosstrek', 'solterra',
        'equinox', 'traverse', 'blazer', 'trailblazer', 'tahoe', 'suburban',
        'terrain', 'acadia', 'yukon',
        'tiguan', 'atlas', 'taos', 'id.4',
        'rogue', 'murano', 'pathfinder', 'armada', 'kicks', 'ariya',
        'outlander', 'eclipse cross', 'outlander sport',
        'rdx', 'mdx', 'zdx',
        'rx', 'nx', 'ux', 'gx', 'lx', 'rz',
        'x1', 'x3', 'x5', 'x7', 'ix',
        'q3', 'q5', 'q7', 'q8', 'e-tron',
        'glc', 'gle', 'gls', 'eqe', 'eqs',
        'model x', 'model y',
        'xc40', 'xc60', 'xc90', 'ex30', 'ex90',
        'wagoneer', 'grand wagoneer',
      ];

      // Known sedan/hatchback models for inference when Vehicle Type is empty
      const knownSedanModels = [
        'corolla', 'camry', 'prius', 'avalon', 'mirai', 'gr86', 'supra', 'yaris',
        'civic', 'accord', 'insight', 'clarity',
        'sentra', 'altima', 'maxima', 'versa', 'leaf',
        'elantra', 'sonata', 'accent', 'ioniq',
        'forte', 'k5', 'rio', 'stinger',
        'mazda3', 'mazda6', 'mx-5',
        'impreza', 'legacy', 'wrx', 'brz',
        'jetta', 'passat', 'arteon', 'golf', 'beetle', 'id.3',
        'malibu', 'camaro', 'corvette', 'bolt',
        'mustang', 'fusion', 'focus', 'fiesta',
        'charger', 'challenger',
        '3 series', '5 series', '7 series', 'i4',
        'a3', 'a4', 'a5', 'a6', 'a7', 'a8',
        'c-class', 'e-class', 's-class', 'cla', 'cle',
        'model 3', 'model s',
        'is', 'es', 'ls',
        'tlx', 'integra',
        's60', 's90',
        'ct4', 'ct5',
        'genesis', 'g70', 'g80', 'g90',
      ];

      // If Vehicle Type is empty, infer from model name
      if (!vehicleType) {
        if (knownSuvModels.some(m => vehicleModel.includes(m))) {
          vehicleType = 'suv';
        } else if (knownSedanModels.some(m => vehicleModel.includes(m))) {
          vehicleType = 'sedan';
        }
      }

      // Collect all variants for all requested types
      const allVariants: string[] = [];
      let searchingForSuv = false;
      for (const t of types) {
        const tLower = t.toLowerCase().trim();
        if (tLower === 'suv') searchingForSuv = true;
        const variants = bodyVariants[tLower] || [tLower];
        allVariants.push(...variants);
        // Also add singular/plural forms
        allVariants.push(tLower);
        if (!tLower.endsWith('s')) allVariants.push(tLower + 's');
        else allVariants.push(tLower.slice(0, -1));
      }

      const bodyMatches = allVariants.some(v => vehicleType.includes(v));
      const suvModelMatch = searchingForSuv && knownSuvModels.some(m => vehicleModel.includes(m));
      
      if (!bodyMatches && !suvModelMatch) {
        return false;
      }
    }

    // Filter by fuel type - use same variants as search-inventory edge function
    if (criteria.fuelType) {
      const fuelTypes = Array.isArray(criteria.fuelType) ? criteria.fuelType : [criteria.fuelType];
      const vehicleFuel = (vehicle.fuel_type || '').toLowerCase().trim();
      const vehicleModelStr = (vehicle.model || vehicle.Model || '').toLowerCase();
      const vehicleTrim = (vehicle.trim || vehicle.Trim || '').toLowerCase();
      
      const fuelVariants: Record<string, string[]> = {
        'gas': ['gas', 'gasoline', 'petrol', 'regular'],
        'electric': ['electric', 'ev', 'bev'],
        'hybrid': ['hybrid', 'hev'],
        'phev': ['phev', 'plug-in hybrid', 'plug in hybrid'],
        'plugin hybrid': ['phev', 'plug-in hybrid', 'plug in hybrid'],
      };
      
      let fuelMatched = false;
      for (const searchFuel of fuelTypes) {
        const searchLower = searchFuel.toLowerCase().trim();
        const variants = fuelVariants[searchLower] || [searchLower];
        
        if (searchLower === 'hybrid') {
          const hasHybridInModelOrTrim = vehicleModelStr.includes('hybrid') || vehicleTrim.includes('hybrid');
          if (variants.some(v => vehicleFuel.includes(v)) || hasHybridInModelOrTrim) {
            fuelMatched = true;
            break;
          }
        } else {
          if (variants.some(v => vehicleFuel.includes(v))) {
            fuelMatched = true;
            break;
          }
        }
      }
      
      if (!fuelMatched) {
        return false;
      }
    }

    // Filter by price range - handle string to number conversion
    const finalPrice = vehicle['Final price'] || vehicle['Final Price'];
    if (finalPrice !== undefined) {
      // Convert string to number if needed
      const priceValue = typeof finalPrice === 'string' ? parseFloat(finalPrice) : finalPrice;
      
      if (!isNaN(priceValue)) {
        if (criteria.minPrice !== undefined && priceValue < criteria.minPrice) {
          return false;
        }
        if (criteria.maxPrice !== undefined && priceValue > criteria.maxPrice) {
          return false;
        }
      }
    }

    return true;
  });
  
  return filtered;
};

export const getInventoryCount = (): number => {
  return sessionInventory?.length || 0;
};

export const isInventoryLoaded = (): boolean => {
  return sessionInventory !== null;
};

export const clearInventorySession = (): void => {
  sessionInventory = null;
  inventoryLoadTimestamp = null;
};

/**
 * Get the current session inventory (for internal use by hydration bridge)
 */
export const getSessionInventory = (): VehicleInventoryItem[] => {
  return sessionInventory || [];
};

/**
 * Ensure inventory is available for hydration by loading from cache if needed.
 * This is synchronous and avoids waiting for async initialization on page reloads.
 */
export const ensureInventoryLoadedForHydration = (): void => {
  if (sessionInventory && sessionInventory.length > 0) {
    return;
  }

  try {
    loadFromCache();
  } catch (error) {
    console.error('❌ Error ensuring inventory for hydration:', error);
  }
};

export const loadInventoryIfEnabled = async (): Promise<void> => {
  try {
    // Get dealer config from DealerManager - ensure authentication is complete
    const { DealerManager } = await import('./dealerManager');
    
    // Wait for dealer authentication to complete
    const dealerConfig = await DealerManager.initializeFromGlobalSettings();
    
    if (!dealerConfig) {
      return;
    }

    // Check if inventory loading is enabled for this dealer
    if (dealerConfig.inventory_load === true) {
      await loadInventoryForSession();
    }
  } catch (error) {
    console.error('❌ Error checking inventory load status:', error);
  }
};
